import { useEffect, useState } from 'react';
import { ApiError, sizeLabel, usd, type PlanDef, type PublicTenant, type RuleSet } from '@ck/sdk';
import { A, api } from './api';
import { Failed, Loading, PageHead, useApi } from './ui';
import { useMarket } from './market';

type Draft = Omit<PlanDef, 'version' | 'updatedAt'> & { version?: number };
type Issue = { path: (string | number)[]; message: string };

const blankRules = (): RuleSet => ({ profitTargetPct: 8, maxDailyLossPct: 5, dailyLossBasis: 'max_of_balance_equity', maxLossPct: 10, maxLossType: 'static', minTradingDays: 3, maxCalendarDays: null });
const clone = <T,>(x: T): T => JSON.parse(JSON.stringify(x));

export function Plans({ tenant, initial }: { tenant: PublicTenant; initial?: string }) {
  const { data, error, reload } = useApi(() => api.get<{ plans: PlanDef[] }>(A('/plans')).then((r) => r.plans));
  const [editing, setEditing] = useState<{ draft: Draft; isNew: boolean } | null>(null);
  const [startedNew, setStartedNew] = useState(false);
  useEffect(() => { if (initial === 'new' && !startedNew && data) { setStartedNew(true); setEditing({ isNew: true, draft: newPlan('prop', data.find((p) => p.brand === 'prop')) }); } }, [data]);
  const { market } = useMarket();
  const challengeMarkets = tenant.markets.filter((m) => m.kind === 'challenge' && (market === 'all' || m.brand === market));

  if (editing) return <Editor key={editing.draft.id} start={editing.draft} isNew={editing.isNew} tenant={tenant} onClose={(saved) => { setEditing(null); if (saved) reload(); }} />;
  if (error) return <Failed error={error} retry={reload} />;
  if (!data) return <Loading />;

  async function toggle(p: PlanDef) {
    await api.patch(A(`/plans/${p.id}/active`), { active: !p.active }).catch(() => undefined);
    reload();
  }

  return (
    <>
      <PageHead kicker="Products" title="Challenges & rules" lead="Every challenge, rule and price lives here. A change makes a new version for new purchases. To move existing accounts onto it, select them under Trading accounts and use Bulk action, Apply the latest rules." />
      {challengeMarkets.length === 0 && <p className="muted">This market has no challenges. Switch to CK Funded, CK Futures or CK Preps in the top bar.</p>}
      {challengeMarkets.map((m) => {
        const plans = data.filter((p) => p.brand === m.brand);
        return (
          <section key={m.brand} className="panel">
            <div className="panel-h">
              <h2><span className="dot" style={{ background: m.accent }} />{m.name}</h2>
              <button className="btn sm" onClick={() => setEditing({ isNew: true, draft: newPlan(m.brand, plans[0]) })}>New plan</button>
            </div>
            {plans.length === 0 ? <p className="muted">No plans yet.</p> : (
              <div className="plist">
                {plans.map((p) => (
                  <article key={p.id} className={`pcard${p.active ? '' : ' off'}`}>
                    <div className="pcard-h">
                      <div><b>{p.name}</b> <span className="muted">· {p.tag}</span></div>
                      <span className="mono muted">v{p.version}</span>
                    </div>
                    <div className="chips">
                      <span className={`pill ${p.active ? 'open' : 'coming_soon'}`}>{p.active ? 'On sale' : 'Archived'}</span>
                      {!p.final && <span className="pill draft">Draft terms</span>}
                      <span className="pill">{p.phases.length - 1 || 'No'} evaluation phase{p.phases.length === 2 ? '' : 's'}</span>
                      <span className="pill">{p.rewardSplitPct}% split</span>
                    </div>
                    <p className="muted small">{Object.entries(p.pricesUsdCents).map(([s, c]) => `${sizeLabel(Number(s))} ${usd(c / 100)}`).join(' · ')}</p>
                    <div className="pcard-a">
                      <button className="btn sm" onClick={() => setEditing({ isNew: false, draft: clone(p) })}>Edit rules</button>
                      <button className="link" onClick={() => setEditing({ isNew: true, draft: { ...clone(p), id: `${p.id}-copy`, name: `${p.name} copy`, active: false } })}>Duplicate</button>
                      <button className="link" onClick={() => toggle(p)}>{p.active ? 'Archive' : 'Put on sale'}</button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        );
      })}
    </>
  );
}

function newPlan(brand: PlanDef['brand'], base?: PlanDef): Draft {
  if (base) return { ...clone(base), id: `${brand}-new`, name: 'New plan', tag: 'Two-step', active: false, final: false };
  return {
    id: `${brand}-new`, brand, name: 'New plan', tag: 'Two-step', description: '', rewardSplitPct: 80, leverage: 100,
    payout: { firstPayoutAfterDays: 14, cycleDays: 14, minProfitableDays: 3, minPayoutUsd: 50 },
    pricesUsdCents: { '100000': 49900 }, active: false, final: false,
    phases: [
      { key: 'p1', name: 'Phase 1', rules: blankRules() },
      { key: 'funded', name: 'Funded', funded: true, rules: { ...blankRules(), profitTargetPct: null, minTradingDays: 0 } },
    ],
  };
}

function Editor({ start, isNew, tenant, onClose }: { start: Draft; isNew: boolean; tenant: PublicTenant; onClose: (saved: boolean) => void }) {
  const [d, setD] = useState<Draft>(start);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (patch: Partial<Draft>) => setD({ ...d, ...patch });
  const setRule = (i: number, patch: Partial<RuleSet>) => set({ phases: d.phases.map((ph, j) => (j === i ? { ...ph, rules: { ...ph.rules, ...patch } } : ph)) });
  const setPhase = (i: number, patch: Partial<Draft['phases'][number]>) => set({ phases: d.phases.map((ph, j) => (j === i ? { ...ph, ...patch } : ph)) });
  const prices = Object.entries(d.pricesUsdCents).sort((a, b) => Number(a[0]) - Number(b[0]));
  const issueFor = (...path: (string | number)[]) => issues.find((x) => x.path.join('.') === path.join('.'))?.message;

  function addPhase() {
    const funded = d.phases[d.phases.length - 1]!;
    const n = d.phases.length;
    set({ phases: [...d.phases.slice(0, -1), { key: `p${n}`, name: `Phase ${n}`, rules: blankRules() }, funded] });
  }

  async function save() {
    setBusy(true); setIssues([]); setErr(null);
    const body = { ...d } as Record<string, unknown>;
    delete body.version; delete body.updatedAt;
    try {
      if (isNew) await api.post(A('/plans'), body); else await api.put(A(`/plans/${d.id}`), body);
      onClose(true);
    } catch (e) {
      if (e instanceof ApiError) { setErr(e.message); setIssues(e.issues ?? []); } else setErr('Save failed');
    } finally { setBusy(false); }
  }

  const num = (v: string) => (v === '' ? null : Number(v));
  return (
    <div className="editor">
      <div className="editor-h">
        <button className="link" onClick={() => onClose(false)}>← All plans</button>
        <h2>{isNew ? 'New plan' : `Edit ${start.name}`} <span className="mono muted">{tenant.markets.find((m) => m.brand === d.brand)?.name}{d.version ? `, saving version ${d.version + 1}` : ''}</span></h2>
      </div>

      <section className="panel">
        <h3>Plan</h3>
        <div className="grid4">
          <label>Plan id<input value={d.id} disabled={!isNew} onChange={(e) => set({ id: e.target.value })} />{issueFor('id') && <em>{issueFor('id')}</em>}</label>
          <label>Name<input value={d.name} onChange={(e) => set({ name: e.target.value })} /></label>
          <label>Tag<input value={d.tag} onChange={(e) => set({ tag: e.target.value })} /></label>
          <label>Reward split %<input type="number" min={1} max={100} value={d.rewardSplitPct} onChange={(e) => set({ rewardSplitPct: Number(e.target.value) })} /></label>
          <label className="span2">Description<input value={d.description} onChange={(e) => set({ description: e.target.value })} /></label>
          <label>Leverage (1:x)<input type="number" min={1} value={d.leverage} onChange={(e) => set({ leverage: Number(e.target.value) })} /></label>
          <div className="toggles">
            <label className="check"><input type="checkbox" checked={d.active} onChange={(e) => set({ active: e.target.checked })} /> On sale</label>
            <label className="check"><input type="checkbox" checked={d.final} onChange={(e) => set({ final: e.target.checked })} /> Final terms</label>
          </div>
        </div>
      </section>

      <section className="panel">
        <div className="panel-h"><h3>Phases &amp; rules</h3><button className="link" onClick={addPhase} disabled={d.phases.length >= 5}>+ Add evaluation phase</button></div>
        {issueFor('phases') && <p className="err">{issueFor('phases')}</p>}
        <div className="tw">
          <table className="tbl edit">
            <thead><tr><th>Phase</th><th>Target %</th><th>Daily loss %</th><th>Daily basis</th><th>Max loss %</th><th>Max loss type</th><th>Min days</th><th>Time limit (days)</th><th /></tr></thead>
            <tbody>
              {d.phases.map((ph, i) => {
                const last = i === d.phases.length - 1;
                return (
                  <tr key={i}>
                    <td><input aria-label="Phase name" value={ph.name} onChange={(e) => setPhase(i, { name: e.target.value })} />{last && <small className="mono muted">Funded stage</small>}</td>
                    <td><input aria-label="Profit target" type="number" step="0.5" disabled={last} value={ph.rules.profitTargetPct ?? ''} onChange={(e) => setRule(i, { profitTargetPct: num(e.target.value) })} />{issueFor('phases', i, 'rules', 'profitTargetPct') && <em>{issueFor('phases', i, 'rules', 'profitTargetPct')}</em>}</td>
                    <td><input aria-label="Daily loss" type="number" step="0.5" placeholder="None" value={ph.rules.maxDailyLossPct ?? ''} onChange={(e) => setRule(i, { maxDailyLossPct: num(e.target.value) })} />{issueFor('phases', i, 'rules', 'maxDailyLossPct') && <em>{issueFor('phases', i, 'rules', 'maxDailyLossPct')}</em>}</td>
                    <td><select aria-label="Daily basis" value={ph.rules.dailyLossBasis} onChange={(e) => setRule(i, { dailyLossBasis: e.target.value as RuleSet['dailyLossBasis'] })}><option value="max_of_balance_equity">Higher of balance/equity</option><option value="balance">Balance</option><option value="equity">Equity</option></select></td>
                    <td><input aria-label="Max loss" type="number" step="0.5" value={ph.rules.maxLossPct} onChange={(e) => setRule(i, { maxLossPct: Number(e.target.value) })} /></td>
                    <td><select aria-label="Max loss type" value={ph.rules.maxLossType} onChange={(e) => setRule(i, { maxLossType: e.target.value as RuleSet['maxLossType'] })}><option value="static">Static</option><option value="trailing">Trailing</option></select></td>
                    <td><input aria-label="Min trading days" type="number" min={0} value={ph.rules.minTradingDays} onChange={(e) => setRule(i, { minTradingDays: Number(e.target.value) })} /></td>
                    <td><input aria-label="Time limit" type="number" min={1} placeholder="Unlimited" value={ph.rules.maxCalendarDays ?? ''} onChange={(e) => setRule(i, { maxCalendarDays: num(e.target.value) })} /></td>
                    <td>{!last && d.phases.length > 2 && <button className="link danger" onClick={() => set({ phases: d.phases.filter((_, j) => j !== i) })}>Remove</button>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <div className="grid2">
        <section className="panel">
          <div className="panel-h"><h3>Sizes &amp; prices (USD)</h3><button className="link" onClick={() => set({ pricesUsdCents: { ...d.pricesUsdCents, [String(Number(prices.at(-1)?.[0] ?? 0) + 50000)]: 9900 } })}>+ Add size</button></div>
          {issueFor('pricesUsdCents') && <p className="err">{issueFor('pricesUsdCents')}</p>}
          <table className="tbl edit"><thead><tr><th>Account size</th><th>Price</th><th /></tr></thead><tbody>
            {prices.map(([size, cents]) => (
              <tr key={size}>
                <td><input aria-label="Account size" type="number" step={1000} defaultValue={size} onBlur={(e) => {
                  const next = String(Number(e.target.value));
                  if (next === size || !Number(next)) return;
                  const p = { ...d.pricesUsdCents }; delete p[size]; p[next] = cents; set({ pricesUsdCents: p });
                }} /></td>
                <td><input aria-label="Price" type="number" step="1" value={cents / 100} onChange={(e) => set({ pricesUsdCents: { ...d.pricesUsdCents, [size]: Math.round(Number(e.target.value) * 100) } })} /></td>
                <td>{prices.length > 1 && <button className="link danger" onClick={() => { const p = { ...d.pricesUsdCents }; delete p[size]; set({ pricesUsdCents: p }); }}>Remove</button>}</td>
              </tr>
            ))}
          </tbody></table>
        </section>
        <section className="panel">
          <h3>Payout policy</h3>
          <div className="grid2 tight">
            <label>First reward after (days)<input type="number" min={0} value={d.payout.firstPayoutAfterDays} onChange={(e) => set({ payout: { ...d.payout, firstPayoutAfterDays: Number(e.target.value) } })} /></label>
            <label>Then every (days)<input type="number" min={1} value={d.payout.cycleDays} onChange={(e) => set({ payout: { ...d.payout, cycleDays: Number(e.target.value) } })} /></label>
            <label>Min profitable days<input type="number" min={0} value={d.payout.minProfitableDays} onChange={(e) => set({ payout: { ...d.payout, minProfitableDays: Number(e.target.value) } })} /></label>
            <label>Min reward (USD)<input type="number" min={0} value={d.payout.minPayoutUsd} onChange={(e) => set({ payout: { ...d.payout, minPayoutUsd: Number(e.target.value) } })} /></label>
          </div>
        </section>
      </div>

      <div className="savebar">
        {err && <p className="err" role="alert">{err}{issues.length > 0 && <>: {issues.map((x) => x.message).join('; ')}</>}</p>}
        {!isNew && <button className="btn ghost" disabled={busy} onClick={async () => {
          if (!confirm('Put this plan back to its original sample rules and prices? This saves a new version.')) return;
          setBusy(true); setErr(null);
          try { await api.post(A(`/plans/${d.id}/reset-defaults`)); onClose(true); } catch (e) { setErr(e instanceof ApiError ? e.message : 'Reset failed'); } finally { setBusy(false); }
        }}>Reset to default rules</button>}
        <span style={{ flex: 1 }} />
        <button className="link" onClick={() => onClose(false)}>Cancel</button>
        <button className="btn" onClick={save} disabled={busy}>{busy ? 'Saving…' : isNew ? 'Create plan' : 'Save new version'}</button>
      </div>
    </div>
  );
}
